<template lang="pug">
.work-orders-table-component
  .table-wrapper.mt-0
    .scrollable
      table.table-work-orders.has-actions.table-hover.table
        thead
          tr
            th Work order
            th Assignees
            th Address
            th Budget
            th Next job
            th
        tbody
          tr(
            v-for="workOrder in allWorkOrders"
            :key="workOrder.id"
          )
            td
              .number-cell-content(:data-qa="workOrderName(workOrder)")
                template(v-if="organization.has_wo_hotwired")
                  a(
                    v-if="!workOrder.archived"
                    :href="showWorkOrderUrl(workOrder.id)"
                  )
                    | {{ workOrderName(workOrder) }}
                template(v-else)
                  InertiaLink(
                    v-if="!workOrder.archived"
                    :href="showWorkOrderUrl(workOrder.id)"
                  )
                    | {{ workOrderName(workOrder) }}
                  span(
                    v-else
                    v-tooltip="{ container: 'body' }"
                    title="Archived work orders cannot be edited"
                  )
                    | {{ workOrderName(workOrder) }}
              small.work-order-status.label.mr-5(
                v-for="woState in workOrder.woStates"
                :key="woState.symbol"
                :class="workOrderStatusStyleClass(woState)"
              )
                | {{ workOrderStateName(woState) }}
            td
              template(v-if="workOrderAssigneesCount(workOrder)")
                HubAvatar.avatar-overlaps(
                  v-for="assignee in workOrderAssignees(workOrder)"
                  :key="assignee.id"
                  v-tooltip="{ delay: { hide: 0 } }"
                  :title="assignee.name"
                  :user-id="assignee.id"
                  avatar-style="speck"
                  :users-avatars="usersAvatars"
                )
                span.more-members-badge.light(v-if="workOrderAssigneesCount(workOrder) > 3") +{{ workOrderAssigneesCount(workOrder) - 3 }}
              template(v-else)
                span.more-members-badge.empty
            td
              .address-cell-content
                template(v-if="workOrderHasAddress(workOrder)") {{ workOrder.jobSite.address }}
                small.text-lighter(v-else) No address added
            td.budget-cell-content-wrapper
              .budget-cell-content(
                v-if="canManageBudget"
                @click.prevent="openEditDialog(workOrder, tabs.budget)"
              )
                template(v-if="workOrder.has_budget")
                  a.budget-is-present.hover-link(
                    v-tooltip="{ html: true, container: 'body' }"
                    :data-original-title="infoBudgetTooltip(workOrder.budget)"
                  )
                    AppProgressBar(
                      :tracked="workOrder.budget.spent"
                      :total="totalProgressBar(workOrder.budget)"
                    )
                    i.hi.hi-edit-xs.edit.hi-12
                template(v-else)
                  a.budget-is-empty.hover-link(
                    v-tooltip="{ placement: 'right', container: 'body' }"
                    title="Edit this work order's budget"
                  )
                    small.text-muted Budget:
                    small.text-default none
                    i.hi.hi-edit-xs.edit.hi-12
            td
              .d-flex.align-items-center(v-if="workOrder.upcomingJob")
                i.hi.hi-refresh.text-muted.mr-5(
                  v-if="jobIsRecurrent(workOrder.upcomingJob)"
                  v-tooltip
                  :title="scheduleTooltip(workOrder.upcomingJob.schedule)"
                )
                div
                  | {{ $filters.formatDate(workOrder.upcomingJob.startsAt) }}
                  br
                  | {{ $filters.formatTimeRangeNoSeconds(workOrder.upcomingJob.startsAt, workOrder.upcomingJob.stopsAt).toLowerCase() }}{{ `${workOrder.timeZoneAbbr ? ` ${workOrder.timeZoneAbbr}` : ''}` }}
              small.text-lighter(v-else) No upcoming jobs
            td
              .dropdown.table-actions-dropdown.work-order-action-dropdown
                a.btn.btn-sm.btn-default.dropdown-toggle(
                  href="#"
                  data-toggle="dropdown"
                  role="button"
                ) Actions
                  i.hi.hi-caret.hi-10
                ul.dropdown-menu.dropdown-menu-right
                  li(v-if="!workOrder.archived")
                    InertiaLink.action(:href="showWorkOrderUrl(workOrder.id)") View
                  li(v-if="!workOrder.archived")
                    a.action(@click="openEditDialog(workOrder)") Edit
                  li
                    template(v-if="workOrder.archived")
                      a.action(@click="confirmRestoreWorkOrder(workOrder)") Reopen
                    template(v-else)
                      a.action(@click="confirmArchiveWorkOrder(workOrder)") Mark as closed
                  // li
                  //   a.action(@click="confirmSoftDeleteWorkOrder(workOrder)") Delete
      WorkOrdersNoData(
        v-if="noWorkOrders"
        @add-work-order="$emit('add-work-order')"
      )
  AppPagination(
    v-if="!noWorkOrders"
    :pagination="pagination"
    :resource="{ one: 'Work order', many: 'Work orders' }"
    @page-changed="onPageChanged"
  )
</template>

<script>
import { get } from 'lodash-es';
import WorkOrdersNoData from './WorkOrdersNoData.vue';
import { WorkOrderActions, RecurringJobs } from '~/mixins';
import { applyTemplate as applyUrlTemplate } from '~/utils/url_template';
import { WORK_ORDERS_URL, WORK_ORDER_DIALOG_TABS, BUDGET_TYPE_HOURS } from '~/app_constants';

import { formatHours } from '~/utils/time_utils';

export default {
  name: 'WorkOrdersTable',
  components: {
    WorkOrdersNoData,
  },
  mixins: [WorkOrderActions, RecurringJobs],
  props: {
    usersAvatars: {
      type: Map,
      required: true,
    },
    allWorkOrders: {
      type: Array,
      required: true,
    },
    pagination: {
      type: Object,
      required: true,
    },
    organization: {
      type: Object,
      required: true,
    },
    canManageBudget: {
      type: Boolean,
      required: true,
    },
    domainQuery: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    tabs: WORK_ORDER_DIALOG_TABS,
  }),
  computed: {
    noWorkOrders() {
      return (this.allWorkOrders || []).length === 0;
    },
  },
  methods: {
    showWorkOrderUrl(workOrderId) {
      const url = applyUrlTemplate(WORK_ORDERS_URL, {
        woId: workOrderId,
        organizationId: this.organization.id,
      });

      return url;
    },
    workOrderHasAddress(workOrder) {
      return workOrder.jobSite && workOrder.jobSite.address;
    },
    workOrderName(workOrder) {
      return workOrder.number && workOrder.client.name ? `${workOrder.number} - ${workOrder.client.name}` : '';
    },
    isHoursType(budget) {
      return get(budget, 'type') === BUDGET_TYPE_HOURS;
    },
    totalProgressBar(budget) {
      return !this.isHoursType(budget) ? budget.cost : budget.hours;
    },
    budgetPercentageUsed(budget) {
      const getPercentage = (initialBudget) => ((budget.spent / initialBudget) * 100).toFixed(0) + '%';

      return !this.isHoursType(budget) ? getPercentage(budget.cost) : getPercentage(budget.hours);
    },
    infoBudgetTooltip(budget) {
      const percentage = this.budgetPercentageUsed(budget);

      return `<div class="text-left">
        <p><strong>Spent</strong>: ${this.budgetTooltipSpentContent(budget, percentage)}<p>
        <p><strong>Remaining</strong>: ${this.budgetTooltipRemainingContent(budget)}</p>
      </div>`;
    },
    budgetTooltipSpentContent(budget, percentage) {
      const { hours, cost, spent } = budget;
      let tooltip = '';

      if (!this.isHoursType(budget)) {
        tooltip = `${spent.formatMoney(this.organization.currency)}/${cost.formatMoney(this.organization.currency)}`;
      } else {
        tooltip = `${formatHours(spent)}/${formatHours(hours)}`;
      }

      return `${tooltip} <strong><em>(${percentage})</em></strong>`;
    },
    budgetTooltipRemainingContent(budget) {
      const { cost, hours, spent } = budget;
      const budgetRemaining = !this.isHoursType(budget) ? cost - spent : hours - spent;
      const percentage = Math.max(0, (budgetRemaining / (!this.isHoursType(budget) ? cost : hours)) * 100);
      const percentageRemaining = percentage.toFixed(0) + '%';
      const remainingFormatted = this.isHoursType(budget)
        ? formatHours(budgetRemaining)
        : budgetRemaining.formatMoney(this.organization.currency);

      return `${remainingFormatted} <strong><em>(${percentageRemaining})</em></strong>`;
    },
    workOrderAssignees(workOrder) {
      return (workOrder.assignees || []).slice(0, 3);
    },
    workOrderAssigneesCount(workOrder) {
      return (workOrder.assignees || []).length;
    },
    workOrderStateName(workOrderState) {
      return workOrderState ? workOrderState.name : '';
    },
    workOrderStatusStyleClass(workOrderState) {
      return workOrderState ? `label-${workOrderState.symbol}` : '';
    },
    openEditDialog(workOrder, tab) {
      this.$emit('open-edit-dialog', workOrder, tab);
    },
    onPageChanged(page) {
      this.$emit('page-changed', page);
    },
  },
};
</script>
